import { apiClient, API_CONFIG } from '../../core';
import type { ApiResponse } from '../../shared';
import { buildQueryString } from '../../shared';
import type {
  LC_SubmitShiftChangeRequestOut,
  LC_GetShiftRequestsIn,
  LC_GetShiftRequestsOut,
  ShiftRequestPage,
  ShiftRequestFormData,
  LC_GetCompanyShiftRequestsIn,
  LC_GetCompanyShiftRequestsOut,
  LC_GetCompanyShiftRequestCountersIn,
  LC_GetCompanyShiftRequestCountersOut,
  LC_ExportCompanyShiftRequestsIn,
  LC_ApproveShiftChangeRequestIn,
  LC_ResolveShiftChangeRequestIn,
  LC_ResolveShiftChangeRequestOut,
  CompanyShiftRequestPage,
  ShiftRequestCounters,
  LC_GetCompanyShiftsListOut,
  CompanyShiftCatalogListResult,
  LC_BulkApproveShiftRequestsIn,
  LC_BulkApproveShiftRequestsOut,
  LC_BulkRejectShiftRequestsIn,
  LC_BulkRejectShiftRequestsOut,
  LC_CreateCompanyShiftIn,
  LC_CreateCompanyShiftOut,
  LC_GetSchedulesListOut,
  ScheduleOptionListResult,
} from './shift.types';
import {
  mapShiftRequestPage,
  mapCompanyShiftRequestPage,
  mapShiftRequestCounters,
  mapFormDataToSubmitRequest,
  mapCompanyShiftCatalogList,
  mapScheduleOptions,
} from './shift.mapper';
import { SHIFT_REQUEST_DECISION } from './shift.types';


export class ShiftService {
  /**
   * Submit a new shift change request for an employee
   */
  submitShiftChangeRequest(
    employeeId: number,
    formData: ShiftRequestFormData,
  ): Promise<ApiResponse<LC_SubmitShiftChangeRequestOut>> {
    const input = mapFormDataToSubmitRequest(formData);
    
    return apiClient.post<LC_SubmitShiftChangeRequestOut>(
      API_CONFIG.ENDPOINTS.SHIFT.SUBMIT_REQUEST(employeeId),
      input,
    );
  }

  /**
   * Get paginated list of shift change requests for an employee
   */
  async getShiftRequests(
    input: LC_GetShiftRequestsIn,
  ): Promise<ApiResponse<ShiftRequestPage>> {
    const queryString = buildQueryString({
      status: input.status,
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      page: input.page,
      pageSize: input.pageSize,
      searchText: input.searchText,
      orderBy: input.orderBy,
      orderDirection: input.orderDirection,
    });

    const endpoint = API_CONFIG.ENDPOINTS.SHIFT.GET_REQUESTS(input.employeeId);
    const fullEndpoint = `${endpoint}${queryString}`;

    const response = await apiClient.get<LC_GetShiftRequestsOut>(fullEndpoint);

    if (!response.success || !response.data) {
      return { success: false, data: null, error: response.error };
    }

    return { success: true, data: mapShiftRequestPage(response.data) };
  }

  /**
   * Get the company-scoped paginated list of shift change requests (admin listing).
   * Company is resolved from the JWT on the backend; results are profiled by access level.
   * The client never sends companyId.
   */
  async getCompanyShiftRequests(
    input: LC_GetCompanyShiftRequestsIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<CompanyShiftRequestPage>> {
    const queryString = buildQueryString({
      status: input.status,
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      employeeIds: input.employeeIds,
      searchText: input.searchText,
      orderBy: input.orderBy,
      orderDirection: input.orderDirection,
      page: input.page,
      pageSize: input.pageSize,
    });

    const endpoint = API_CONFIG.ENDPOINTS.SHIFT.GET_COMPANY_REQUESTS();

    // The backend hoists pagination to the envelope root, so apiClient returns the
    // LC_PagedResult directly in `response.data` (same shape as getShiftRequests).
    const response = await apiClient.get<LC_GetCompanyShiftRequestsOut>(
      `${endpoint}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapCompanyShiftRequestPage(response.data) };
  }

  /**
   * Get the KPI status counters (ASIS-5237) for the company-scoped listing.
   * Dedicated endpoint that applies the SAME filters as the listing (status included);
   * companyId is resolved from the JWT on the backend.
   */
  async getCompanyShiftRequestCounters(
    input: LC_GetCompanyShiftRequestCountersIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<ShiftRequestCounters>> {
    const queryString = buildQueryString({
      status: input.status,
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      employeeIds: input.employeeIds,
      searchText: input.searchText,
    });

    const endpoint = API_CONFIG.ENDPOINTS.SHIFT.GET_COMPANY_REQUEST_COUNTERS();

    const response = await apiClient.get<LC_GetCompanyShiftRequestCountersOut>(
      `${endpoint}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapShiftRequestCounters(response.data) };
  }

  /**
   * Export the company-scoped shift change requests (ASIS-5238) as an Excel file.
   * Applies the SAME filters as the listing; companyId is resolved from the JWT on
   * the backend (never sent by the client). Returns the blob and the filename
   * extracted from the Content-Disposition response header.
   */
  exportCompanyShiftRequests(
    filters: LC_ExportCompanyShiftRequestsIn = {},
    signal?: AbortSignal,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString({
      status: filters.status,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      employeeIds: filters.employeeIds,
      searchText: filters.searchText,
    });

    const endpoint = `${API_CONFIG.ENDPOINTS.SHIFT.EXPORT_COMPANY_REQUESTS}${queryString}`;

    return apiClient.downloadBlobWithFilename(endpoint, { method: 'GET', signal });
  }

  /**
   * Resolve a company-scoped shift change request (ASIS-5234) — unified endpoint.
   * Approves OR rejects based on the `decision` discriminator. Sends the mandatory
   * approver comment; companyId, approver and request id are resolved on the
   * backend (JWT + route). Always returns HTTP 200 with an `outcome` discriminator
   * ('approved' | 'rejected' | 'alreadyResolved'); the latter carries the real
   * current state for concurrency handling.
   */
  resolveShiftChangeRequest(
    requestId: number,
    input: LC_ResolveShiftChangeRequestIn,
  ): Promise<ApiResponse<LC_ResolveShiftChangeRequestOut>> {
    return apiClient.put<LC_ResolveShiftChangeRequestOut>(
      API_CONFIG.ENDPOINTS.SHIFT.RESOLVE_REQUEST(requestId),
      input,
    );
  }

  /**
   * Approve a company-scoped shift change request (ASIS-5233).
   * Thin wrapper over {@link resolveShiftChangeRequest} that fixes the decision to
   * 'approved'; the contract for callers (only the approver comment) is unchanged.
   */
  approveShiftChangeRequest(
    requestId: number,
    input: LC_ApproveShiftChangeRequestIn,
  ): Promise<ApiResponse<LC_ResolveShiftChangeRequestOut>> {
    return this.resolveShiftChangeRequest(requestId, {
      decision: SHIFT_REQUEST_DECISION.APPROVED,
      approverComment: input.approverComment,
    });
  }

  /**
   * Reject a company-scoped shift change request (ASIS-5234).
   * Thin wrapper over {@link resolveShiftChangeRequest} that fixes the decision to
   * 'rejected'; the contract for callers (only the approver comment) is unchanged.
   */
  rejectShiftChangeRequest(
    requestId: number,
    input: LC_ResolveShiftChangeRequestIn,
  ): Promise<ApiResponse<LC_ResolveShiftChangeRequestOut>> {
    return this.resolveShiftChangeRequest(requestId, {
      decision: SHIFT_REQUEST_DECISION.REJECTED,
      approverComment: input.approverComment,
    });
  }

  /**
   * Bulk approve company-scoped shift change requests (ASIS-5235).
   * Sends a CSV of request IDs and a mandatory approver comment.
   * Returns per-item outcomes and aggregated totals.
   */
  bulkApproveShiftRequests(
    input: LC_BulkApproveShiftRequestsIn,
  ): Promise<ApiResponse<LC_BulkApproveShiftRequestsOut>> {
    return apiClient.put<LC_BulkApproveShiftRequestsOut>(
      API_CONFIG.ENDPOINTS.SHIFT.BULK_APPROVE_REQUESTS,
      input,
    );
  }

  /**
   * Bulk reject company-scoped shift change requests (ASIS-5236).
   * Sends a CSV of request IDs and a mandatory approver comment.
   * Returns per-item outcomes and aggregated totals.
   */
  bulkRejectShiftRequests(
    input: LC_BulkRejectShiftRequestsIn,
  ): Promise<ApiResponse<LC_BulkRejectShiftRequestsOut>> {
    return apiClient.put<LC_BulkRejectShiftRequestsOut>(
      API_CONFIG.ENDPOINTS.SHIFT.BULK_REJECT_REQUESTS,
      input,
    );
  }

  /**
   * Get the paginated catalog of company shifts (timTurno) — ASIS-5035.
   * Company is resolved from the JWT on the backend; never sent by the client.
   */
  async getCompanyShifts(
    params: {
      page?: number;
      pageSize?: number;
      searchText?: string;
      orderBy?: string;
      orderDirection?: string;
      workingDayType?: string;
      workingDayId?: number;
      status?: string;
    },
    signal?: AbortSignal,
  ): Promise<ApiResponse<CompanyShiftCatalogListResult>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
      workingDayType: params.workingDayType,
      workingDayId: params.workingDayId,
      status: params.status,
    });

    const response = await apiClient.get<LC_GetCompanyShiftsListOut>(
      `${API_CONFIG.ENDPOINTS.SHIFT.GET_COMPANY_SHIFTS}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapCompanyShiftCatalogList(response.data) };
  }

  /**
   * Create a company shift (timTurno) — header + cycle day distribution — ASIS-5037.
   * Company, employee and user identity are resolved from the JWT on the backend.
   */
  createCompanyShift(
    input: LC_CreateCompanyShiftIn,
  ): Promise<ApiResponse<LC_CreateCompanyShiftOut>> {
    return apiClient.post<LC_CreateCompanyShiftOut>(
      API_CONFIG.ENDPOINTS.SHIFT.CREATE_COMPANY_SHIFT,
      input,
    );
  }

  /**
   * Get the paginated catalog of company schedules (caHorario) for shift-day
   * assignment — ASIS-5052. Company is resolved from the JWT; never sent by the client.
   */
  async getSchedules(
    params: { pageSize?: number } = {},
    signal?: AbortSignal,
  ): Promise<ApiResponse<ScheduleOptionListResult>> {
    const queryString = buildQueryString({ pageSize: params.pageSize });

    const response = await apiClient.get<LC_GetSchedulesListOut>(
      `${API_CONFIG.ENDPOINTS.SHIFT.GET_SCHEDULES}${queryString}`,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapScheduleOptions(response.data) };
  }

  /**
   * Delete a shift change request (if backend supports it)
   * TODO: Check if backend has DELETE endpoint
   */
  deleteShiftRequest(
    employeeId: number,
    requestId: number,
  ): Promise<ApiResponse<void>> {
    // Placeholder - needs backend endpoint confirmation
    return apiClient.delete<void>(
      `${API_CONFIG.ENDPOINTS.SHIFT.SUBMIT_REQUEST(employeeId)}/${requestId}`,
    );
  }
}

export const shiftService = new ShiftService();
